import React, { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { ChannelPageHero } from '@/pages/WatchPage';
import { motion } from 'framer-motion';
import { Upload, CheckCircle2, Loader2 } from 'lucide-react';

const FLOATING_WORDS = ['Collaborate', 'Create', 'Build', 'Innovate', 'Opportunity', 'Imagine', 'Partner', 'Sponsor', 'Podcast', 'Music', 'Community', 'Growth', 'Strategy', 'Vision', 'Future', 'Impact'];

function FloatingWords() {
  const positioned = useMemo(() => FLOATING_WORDS.map((w, i) => ({
    word: w,
    top: `${10 + Math.random() * 70}%`,
    left: `${5 + Math.random() * 85}%`,
    size: `${1 + Math.random() * 2.5}rem`,
    delay: `${i * 1.1}s`,
  })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {positioned.map((p, i) => (
        <span key={i} className="pg-floating-word" style={{ top: p.top, left: p.left, fontSize: p.size, animationDelay: p.delay }}>{p.word.toUpperCase()}</span>
      ))}
    </div>
  );
}

export default function CollabPage() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category: '', name: '', email: '', organization: '', subject: '', message: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/collab/categories').then(({ data }) => {
      setCategories(data);
      setForm((f) => ({ ...f, category: data[0]?.id || '' }));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/collab/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile({ url: data.url, name: data.name });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const payload = { ...form, file_url: file?.url || null, file_name: file?.name || null };
      const { data } = await api.post('/collab/submit', payload);
      setSuccess(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="relative">
      <FloatingWords />
      <ChannelPageHero kicker="CHANNEL — COLLAB" title="COLLAB WITH G" blurb="Partnerships, sponsorships, interviews, and conversations. Tell me what you’re building." accent="gold" />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pb-24 relative">
        {success ? (
          <motion.div initial={{ opacity: 1, y: 10 }} animate={{ opacity: 1, y: 0 }} className="pg-card p-10 text-center" style={{ borderColor: 'rgba(226,199,122,0.30)' }} data-testid="collab-success">
            <CheckCircle2 size={40} className="mx-auto text-[var(--pg-gold-2)]" />
            <p className="pg-text-glow-gold font-mono text-[11px] tracking-[0.32em] mt-4">RECEIVED</p>
            <h2 className="font-display text-3xl mt-2 tracking-[0.04em]">THANK YOU.</h2>
            <p className="text-[var(--pg-ink)] mt-3">{success.message}</p>
            <p className="text-xs font-mono tracking-widest text-[var(--pg-gold-2)] mt-4">REF · {success.reference}</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="pg-card p-6 sm:p-8 space-y-5" style={{ borderColor: 'rgba(226,199,122,0.22)' }}>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Category</label>
              <select value={form.category} onChange={set('category')} data-testid="collab-category" className="mt-1 w-full pg-input rounded-full px-4 py-3 text-sm">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Your Name</label>
                <input required value={form.name} onChange={set('name')} data-testid="collab-name" className="mt-1 w-full pg-input rounded-full px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Email</label>
                <input required type="email" value={form.email} onChange={set('email')} data-testid="collab-email" className="mt-1 w-full pg-input rounded-full px-4 py-3 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Organization (optional)</label>
              <input value={form.organization} onChange={set('organization')} className="mt-1 w-full pg-input rounded-full px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Subject</label>
              <input required value={form.subject} onChange={set('subject')} data-testid="collab-subject" className="mt-1 w-full pg-input rounded-full px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Tell me what you’re building</label>
              <textarea required rows={6} value={form.message} onChange={set('message')} data-testid="collab-message" className="mt-1 w-full pg-input rounded-2xl px-4 py-3 text-sm leading-relaxed" />
            </div>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Attachment (optional)</label>
              <label className="mt-1 pg-btn-ghost cursor-pointer inline-flex">
                <Upload size={14} /> {uploading ? 'Uploading…' : (file ? `Replace (${file.name})` : 'Add a file')}
                <input type="file" hidden onChange={onFile} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.mp3,.wav,.m4a,.mp4,.zip" />
              </label>
              {file && <p className="text-[11px] font-mono text-[var(--pg-gold-2)] mt-2 break-all">✓ {file.name}</p>}
            </div>
            {error && <p className="text-xs text-[var(--pg-neon-pink)] font-mono">{error}</p>}
            <button type="submit" disabled={submitting} data-testid="collab-submit" className="pg-btn-gold w-full justify-center !py-3 disabled:opacity-60">
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : 'Send to G →'}
            </button>
            <p className="text-[11px] text-[var(--pg-ink-faint)] font-mono text-center">Music continues uninterrupted. Expect a response within a few business days.</p>
          </form>
        )}
      </section>
    </div>
  );
}
